/**
 * DocuVaultStorageMeter
 * ─────────────────────
 * Displays a visual storage usage bar for a DocuVault plan.
 * Receives `usedMb` and `limitMb` as props — the parent is responsible
 * for sourcing `limitMb` from loadStorageTiers() (Plans API), not hardcoded values.
 */
import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { formatStorage } from '../../services/subscriptionService';

const { FiHardDrive, FiZap, FiInfo } = FiIcons;

const DocuVaultStorageMeter = ({ usedMb, limitMb, planName, onUpgrade, isLiveData = false }) => {
  const pct      = limitMb > 0 ? Math.min((usedMb / limitMb) * 100, 100) : 0;
  const isAlmost = pct >= 80;
  const isFull   = pct >= 100;

  const barColor  = isFull ? 'bg-red-500' : isAlmost ? 'bg-amber-500' : 'bg-accent-gold';
  const textColor = isFull ? 'text-red-600' : isAlmost ? 'text-amber-600' : 'text-accent-navy';

  return (
    <div className="bg-white border border-brand-dark rounded-[28px] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-brand-light rounded-xl flex items-center justify-center border border-brand-dark">
            <SafeIcon icon={FiHardDrive} className="w-4 h-4 text-accent-navy" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Storage Used</p>
              {isLiveData && (
                <span className="text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 bg-indigo-100 text-indigo-600 border border-indigo-200 rounded-full">
                  Live
                </span>
              )}
            </div>
            <p className="text-[9px] text-text-muted">{planName} Plan</p>
          </div>
        </div>
        <p className={`text-xs font-black ${textColor}`}>
          {formatStorage(usedMb)} / {formatStorage(limitMb)}
        </p>
      </div>

      {/* Progress bar */}
      <div className="h-2.5 bg-brand-light rounded-full overflow-hidden border border-brand-dark mb-3">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className={`h-full rounded-full ${barColor}`}
        />
      </div>

      {/* Percentage label */}
      <div className="flex items-center justify-between mb-1">
        <p className="text-[9px] text-text-muted">{pct.toFixed(1)}% used</p>
        <p className="text-[9px] text-text-muted">{formatStorage(limitMb - usedMb)} remaining</p>
      </div>

      {/* Warning / Upgrade CTA */}
      {(isAlmost || isFull) && (
        <div className={`flex items-center justify-between p-3 rounded-xl border text-[10px] font-bold mt-2 ${
          isFull
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          <span>
            {isFull
              ? 'Storage full — uploads blocked.'
              : `Only ${formatStorage(limitMb - usedMb)} left`}
          </span>
          <button
            onClick={onUpgrade}
            className="flex items-center gap-1 px-3 py-1.5 bg-accent-navy text-white rounded-lg hover:bg-accent-navy/90 transition-all"
          >
            <SafeIcon icon={FiZap} className="w-3 h-3" />
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
};

export default DocuVaultStorageMeter;